import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getHifi, preloadHifi } from '../hifi-assets.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Contrat du rig partagé : interdit. La photo est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'hifi',
  family: 'photographic',
  danceability: 0.68,
  energyLevel: 0.60,
  bpmMin: 70,
  bpmMax: 135,
  movement: 0.40,
  tonality: 0.80,
  enabled: true
};

// HiFi : une chaîne hi-fi des années 70, à partir de DEUX photos identiques
// (tout éteint, vumètres rétroéclairés ; tools/build-hifi.mjs, R8). Les
// aiguilles des vumètres dansent avec la musique, les bobines du
// magnétophone tournent, la platine joue la pochette. Funk, soul, rock
// classique, jazz, disco, pop. Gamme 0,55-0,80 (R9) : c'est un équipement,
// pas un show - rien sur la mesure ni la phrase, aucun flash.
//
// UN SEUL QUAD, la photo, en lumière LINÉAIRE :
//     couleur = off + diff x intensité(groupe du pixel)
// groupes de la carte : rétroéclairage (tremble très légèrement, lampe à
// incandescence), voyant PEAK gauche, PEAK droit, voyant POWER. Tout à 1 :
// radio-on (aiguilles photographiées effacées au build ; ?hifiexact=1).
//
// LES VUMÈTRES (quatre dans la photo : deux sur l'ampli, deux sur le
// magnétophone ; gauche = canal gauche) : aiguille procédurale par DISTANCE
// EN PIXELS (R1), noire, légèrement effilée, son ombre portée décalée de 2 px
// et floue, coupée au masque de la fenêtre. Le mouvement est celui d'un vrai
// VU-mètre : un système du second ordre (ω = 10,9 rad/s, ζ = 0,7 - 300 ms
// pour monter comme pour retomber, ~5 % de dépassement), nourri d'un niveau
// redressé ; le temps n'est jamais posé sur l'aiguille, il nourrit l'entrée.
//
// LES BOBINES : la région du disque tourne (rotation de l'UV de la photo
// autour du centre MESURÉ du flasque : le contour, un cercle, ne bouge pas).
// Un point tourné qui tombe hors du cadre (les bobines sont coupées en haut)
// est lu 120° plus loin : trois rayons, trois trous, la bobine se répète.
// La débitrice (gauche) accélère à mesure que la bande la quitte.
//
// LA PLATINE : la pochette en disque sur le plateau, par l'homographie
// mesurée, à 33 1/3 tours par minute ; le bras, la tête et l'axe (masque
// au build) restent par-dessus.

// VU ballistics: measured with this integrator (60 fps, 4 substeps), a step
// reaches 99 % in 300 ms and overshoots 4 % - at w = 15 it was 217 ms
const OMEGA = 10.9, ZETA = 0.7;
const PEAK_AT = 0.75;                  // the last quarter: in the red
const PEAK_HOLD_S = 0.15;
const RPM_33 = 2 * Math.PI * (100 / 3) / 60;
const REEL_TAPE_SPEED = 26;            // px of tape per second at the hub radius ratio below
const REEL_PACK = [0.36, 0.95];        // tape pack radius, empty .. full, in reel radii
const NO_BPM = 110;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const FLICKER = 0.012;                 // incandescent: barely
const DISC_R = '0.97';                 // the disc's radius, in platter radii (GLSL literal)

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const vertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// highp, the documented exception to mediump: the shader works in IMAGE
// PIXELS (up to 1376) and needs a sub-pixel needle; mediump's 1/1024 relative
// step is more than a pixel there.
const fragment = `
  precision highp float;
  uniform sampler2D uOff;
  uniform sampler2D uDiff;
  uniform sampler2D uIds;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec2 uImg;              // image size in px
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  uniform float uPxPerImg;        // screen px per image px
  uniform float uLight[5];        // per group: 0 none, 1 backlight, 2 peak L, 3 peak R, 4 power
  uniform vec4 uMeter[4];         // pivot x, pivot y, radius, window id
  uniform float uNeedle[4];       // angle, radians from vertical
  uniform float uNeedleOn;
  uniform vec4 uReel[2];          // centre x, y, radius, angle
  uniform mat3 uHinv;             // image px -> the platter's circle (rim = 1, centre = spindle)
  uniform float uPlatter;
  uniform vec2 uAxis;
  uniform float uDisc;            // platter angle
  uniform float uMask;
  varying vec2 vUv;

  vec2 rot(vec2 p, float a) { float c = cos(a), s = sin(a); return vec2(c * p.x - s * p.y, s * p.x + c * p.y); }
  // the photograph at an image position (px, y down), lit by the groups
  vec3 photo(vec2 ip, out float group, out float win, out float keep) {
    vec2 uv = vec2(ip.x / uImg.x, 1.0 - ip.y / uImg.y);
    vec4 id = texture2D(uIds, uv) * 255.0;
    group = id.r; win = id.g; keep = id.a / 255.0;
    float I = 0.0;
    for (int k = 0; k < 5; k++) if (abs(float(k) - group) < 0.5) I = uLight[k];
    return texture2D(uOff, uv).rgb + texture2D(uDiff, uv).rgb * I;
  }
  // a point of a reel that fell off the top of the frame, fetched 120 deg round
  vec2 inFrame(vec2 q, vec2 c) {
    if (q.y >= 1.0) return q;
    vec2 a = c + rot(q - c, 2.0943951);
    if (a.y >= 1.0) return a;
    return c + rot(q - c, -2.0943951);
  }
  float segDist(vec2 p, vec2 a, vec2 b, out float t) {
    vec2 pa = p - a, ba = b - a;
    t = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * t);
  }

  void main() {
    vec2 uvImg = vUv * uUvScale + uUvOffset;              // cover fit
    vec2 ip = vec2(uvImg.x * uImg.x, (1.0 - uvImg.y) * uImg.y);   // image px, y down
    float onePx = 1.0 / uPxPerImg;                        // one screen px in image px

    // --- the reels: the disc turns about its measured centre
    vec2 src = ip;
    for (int k = 0; k < 2; k++) {
      vec4 R = uReel[k];
      vec2 d = ip - R.xy;
      float r = length(d);
      // the outermost 2 px stay put: the contour never moves
      float inside = 1.0 - smoothstep(R.z - 3.0 * onePx, R.z - 1.0 * onePx, r);
      if (inside > 0.0) src = mix(ip, inFrame(R.xy + rot(d, -R.w), R.xy), inside);
    }
    float group, win, keep;
    vec3 col = photo(src, group, win, keep);

    // --- the platter: the cover as a disc lying ON the platter, under the arm.
    // uHinv takes an image pixel to the platter's own circle (measured rim =
    // radius 1, centre = the spindle); the disc is radius DISC_R there, and
    // it turns in that frame, so the label stays stuck to the platter.
    // (uHinv is scaled so that w > 0 on the platter: beyond the map's line at
    // infinity w changes sign, and the circle test would fire again there - a
    // line across the whole frame)
    vec3 h = uHinv * vec3(ip, 1.0);
    vec2 dsk = h.z > 1e-4 ? h.xy / h.z : vec2(1e3);
    float rr = length(dsk);
    // the same test 2 px (screen) higher: a pixel just under the disc's front arc
    vec3 h2 = uHinv * vec3(ip - vec2(0.0, 2.0 * onePx), 1.0);
    float rrUp = h2.z > 1e-4 ? length(h2.xy / h2.z) : 1e3;
    vec3 h6 = uHinv * vec3(ip - vec2(0.0, 6.0 * onePx), 1.0);
    float rrUp6 = h6.z > 1e-4 ? length(h6.xy / h6.z) : 1e3;
    if (uHasCover > 0.5) {
      // near the horizon rr changes by huge steps from pixel to pixel: the
      // antialiasing width is capped, or a whole row reads as 'half inside'
      float dr = min(fwidth(rr), 0.04);
      float inside = rr < 2.0 ? 1.0 - smoothstep(${DISC_R} - dr, ${DISC_R} + dr, rr) : 0.0;
      if (inside > 0.0) {
        vec2 cuv = rot(vec2(dsk.x, -dsk.y), uDisc) * (0.5 / ${DISC_R}) + 0.5;
        vec3 cover = texture2D(uCover, vec2(cuv.x, 1.0 - cuv.y)).rgb;
        // a record in a dim room: the cover darkened, the photo's glints kept
        vec3 rec = cover * 0.42 + col * 0.55;
        col = mix(col, rec, inside * (1.0 - keep));
      }
      // THICKNESS: a 2 px dark band along the FRONT arc (disc y > 0 is the
      // near side), and a CONTACT SHADOW on the platter just under it
      float front = step(0.0, dsk.y);
      float band = (1.0 - inside) * step(rrUp, ${DISC_R}) * front;
      float shadow = (1.0 - inside) * (1.0 - band) * step(rrUp6, ${DISC_R}) * front
                   * (1.0 - smoothstep(${DISC_R}, ${DISC_R} + 0.06, rr));
      col *= (1.0 - 0.80 * band * (1.0 - keep)) * (1.0 - 0.35 * shadow * (1.0 - keep));
    }
    if (uPlatter > 0.5) {
      // ?hifiplatter=1: the measured platter rim (green), the disc outline (red)
      float ew = min(fwidth(rr), 0.04) * 1.2;
      if (rr < 2.0 && abs(rr - 1.0) < ew) col = vec3(0.0, 1.0, 0.0);
      if (rr < 2.0 && abs(rr - ${DISC_R}) < ew) col = vec3(1.0, 0.0, 0.0);
      if (length(ip - uAxis) < 2.0 * onePx) col = vec3(0.0, 1.0, 1.0);
    }

    // --- the needles: in the window they belong to, by distance in screen px
    if (uNeedleOn > 0.5 && win > 0.5) {
      for (int k = 0; k < 4; k++) {
        vec4 M = uMeter[k];
        if (abs(M.w - win) > 0.5) continue;
        float a = uNeedle[k];
        vec2 dir = vec2(sin(a), -cos(a));
        vec2 base = M.xy + dir * (M.z * 0.55);
        vec2 tip = M.xy + dir * (M.z + 6.0);
        float t;
        float dPx = segDist(ip, base, tip, t) * uPxPerImg;
        // slightly tapered: 1.5 px wide at the window bottom, 0.9 at the tip
        float hw = mix(0.75, 0.45, t);
        float needle = 1.0 - smoothstep(hw - 0.5, hw + 0.5, dPx);
        // its shadow on the scale: 2 px down-right, blurred
        float ts;
        float sPx = segDist(ip - vec2(2.0, 2.0) * onePx, base, tip, ts) * uPxPerImg;
        float shadow = (1.0 - smoothstep(hw, hw + 2.5, sPx)) * 0.35;
        col *= (1.0 - shadow) * (1.0 - 0.92 * needle);
      }
    }

    if (uMask > 0.5) {
      // ?hifimask=1: windows blue, groups coloured, the platter keep mask cyan
      if (win > 0.5) col = mix(col, vec3(0.2, 0.3, 1.0), 0.45);
      if (group > 1.5) col = vec3(group == 2.0 ? 1.0 : 0.0, group == 3.0 ? 1.0 : 0.0, group == 4.0 ? 1.0 : 0.0);
      if (keep > 0.5) col = vec3(0.0, 1.0, 1.0);
      for (int k = 0; k < 2; k++) { float e = abs(length(ip - uReel[k].xy) - uReel[k].z); if (e < 1.5 * onePx) col = vec3(1.0, 1.0, 0.0); }
      vec3 h = uHinv * vec3(ip, 1.0); float rr = length(h.xy / h.z);
      if (abs(rr - 1.0) < 0.012) col = vec3(1.0, 0.5, 0.0);
      for (int k = 0; k < 4; k++) if (length(ip - uMeter[k].xy) < 3.0) col = vec3(0.0, 1.0, 0.0);
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class HifiVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._debugMask = urlParam('hifimask') === '1';
    this._debugPlatter = urlParam('hifiplatter') === '1';
    const f = parseFloat(urlParam('hifineedle'));
    this._forceNeedle = Number.isFinite(f) ? clamp01(f) : null;
    this._exact = urlParam('hifiexact') === '1';
    this._post = this._exact
      ? { bloomStrength: 0, vignette: 0, grain: 0, aberration: 0, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 }
      : { bloomStrength: 0.30, bloomThreshold: 0.85, vignette: 0.30, grain: 0.012, aberration: 0.0008, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    const assets = getHifi();
    this._assets = assets;
    this._ready = !!assets;
    if (!assets) {
      const self = this;
      preloadHifi().then(function (ok) {
        if (!ok || !self._scene || self._assets) return;
        const s = self._scene, c = self._camera;
        self.dispose(); self.init(s, c);
      });
      return;
    }
    const d = assets.data;
    this._data = d;
    this._imageAspect = d.size[0] / d.size[1];
    const meters = [], needles = [];
    for (let k = 0; k < 4; k++) {
      const m = d.meters[k];
      meters.push(new THREE.Vector4(m.pivot[0], m.pivot[1], m.r, m.window));
      needles.push(0);
    }
    const reels = d.reels.map((r) => new THREE.Vector4(r.c[0], r.c[1], r.r, 0));
    const Hi = d.platter.Hinv;
    this._u = {
      uOff: { value: assets.off }, uDiff: { value: assets.diff }, uIds: { value: assets.ids },
      uCover: { value: null }, uHasCover: { value: 0 },
      uImg: { value: new THREE.Vector2(d.size[0], d.size[1]) },
      uUvScale: { value: new THREE.Vector2(1, 1) }, uUvOffset: { value: new THREE.Vector2(0, 0) },
      uPxPerImg: { value: 1 },
      uLight: { value: [0, 1, 0, 0, 1] },
      uMeter: { value: meters }, uNeedle: { value: needles }, uNeedleOn: { value: this._exact ? 0 : 1 },
      uReel: { value: reels },
      // row-major data -> three's column-major Matrix3.set takes row-major
      uHinv: { value: (function () {
        // scale so that w > 0 at the axis (the platter side of the horizon)
        const ax = d.platter.axis, w = Hi[6] * ax[0] + Hi[7] * ax[1] + Hi[8], k = w < 0 ? -1 : 1;
        return new THREE.Matrix3().set(Hi[0] * k, Hi[1] * k, Hi[2] * k, Hi[3] * k, Hi[4] * k, Hi[5] * k, Hi[6] * k, Hi[7] * k, Hi[8] * k);
      })() },
      uDisc: { value: 0 }, uMask: { value: this._debugMask ? 1 : 0 },
      uPlatter: { value: this._debugPlatter ? 1 : 0 }, uAxis: { value: new THREE.Vector2(d.platter.axis[0], d.platter.axis[1]) }
    };
    this._mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._u, vertexShader: vertex, fragmentShader: fragment, depthTest: false, depthWrite: false }));
    this._mesh.frustumCulled = false; this._mesh.renderOrder = -3000;
    const self = this;
    this._size = new THREE.Vector2();
    this._mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(self._size);
      self._u.uPxPerImg.value = self._size.y / (d.size[1] * self._u.uUvScale.value.y);
    };
    scene.add(this._mesh);

    // state
    this._time = 0;
    this._x = [0, 0]; this._v = [0, 0];          // needle position / velocity per channel
    this._peakUntil = [-1, -1]; this._peakDropUntil = -1;
    this._reelAngle = [0, 0]; this._disc = 0;
    this._phaseBeat = null; this._free = 0; this._beatAge = 10;
    this._dropUntil = -1;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._coverUrl = undefined; this._coverTex = null;
    this._seedL = Math.random() * 100; this._seedR = Math.random() * 100;
    this._aspect = 0;
    this._reframe();
  }

  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    const u = this._u;
    if (aspect > this._imageAspect) {
      const k = this._imageAspect / aspect;
      u.uUvScale.value.set(1, k); u.uUvOffset.value.set(0, (1 - k) / 2);
    } else {
      const k = aspect / this._imageAspect;
      u.uUvScale.value.set(k, 1); u.uUvOffset.value.set((1 - k) / 2, 0);
    }
  }

  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._u.uHasCover.value = 0;
    if (!profile.artworkUrl || this._exact) return;   // no cover: the record stays the photo's vinyl
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._u.uCover.value = tex; self._u.uHasCover.value = 1;
    }, () => {});
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { if (this._ready) { this._loadCover(profile); this._dropIdx = 0; this._lastNowMs = null; } }

  update(deltaTime, profile) {
    if (!this._ready) return;
    const aspect = safeZoneUniforms.uAspect.value || 0;
    if (aspect && aspect !== this._aspect) this._reframe();
    this._loadCover(profile);
    // pause: everything holds - needles, reels, record - never black
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    const t = this._time;
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase;
    const grid = bp != null && bpm > 0;
    const beatS = 60 / (grid ? bpm : NO_BPM), barS = 4 * beatS;
    let beat = false;
    if (grid) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; } }
    if (beat) this._beatAge = 0;
    this._beatAge += dt;
    if (grid && this._tickDrops(profile)) { this._dropUntil = t + barS; }
    const inDrop = t < this._dropUntil;

    // --- the level each channel's meter is fed: a rectified signal, never a pose
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const energy = director && director.intensity != null ? clamp01(director.intensity)
      : (profile.trackFeatures && profile.trackFeatures.energy != null ? profile.trackFeatures.energy / 100 : 0.5);
    const low = bands ? clamp01(bands.low) : energy * 0.8, mid = bands ? clamp01(bands.mid) : energy * 0.7;
    const kick = Math.exp(-this._beatAge / 0.11);          // the beat feeds the input, the ballistics do the rest
    // two decorrelated wanderings: a real stereo pair never moves together
    const wl = 0.5 + 0.5 * Math.sin(t * 1.3 + this._seedL) * Math.sin(t * 0.47 + this._seedL * 2.1);
    const wr = 0.5 + 0.5 * Math.sin(t * 1.1 + this._seedR) * Math.sin(t * 0.53 + this._seedR * 1.7);
    const level = [
      0.18 + 0.38 * (0.62 * low + 0.38 * mid) + 0.14 * wl + 0.30 * kick * (0.5 + low),
      0.18 + 0.38 * (0.38 * low + 0.62 * mid) + 0.14 * wr + 0.28 * kick * (0.5 + mid)
    ];
    if (inDrop) { level[0] = 1.06; level[1] = 1.04; }        // both into the red, pinned for the bar

    // --- VU ballistics: x'' = w^2 (u - x) - 2 zeta w x'  (substeps)
    const steps = 4, h = dt / steps;
    for (let c = 0; c < 2; c++) {
      const u = Math.min(1.12, level[c]);
      for (let s = 0; s < steps; s++) {
        const a = OMEGA * OMEGA * (u - this._x[c]) - 2 * ZETA * OMEGA * this._v[c];
        this._v[c] += a * h;
        this._x[c] += this._v[c] * h;
      }
      // the needle's end stops
      if (this._x[c] < -0.02) { this._x[c] = -0.02; this._v[c] = Math.max(0, this._v[c]); }
      if (this._x[c] > 1.06) { this._x[c] = 1.06; this._v[c] = Math.min(0, this._v[c]); }
      if (this._x[c] > PEAK_AT) this._peakUntil[c] = t + PEAK_HOLD_S;
    }
    const meters = this._data.meters;
    for (let k = 0; k < 4; k++) {
      const m = meters[k];
      const x = this._forceNeedle != null ? this._forceNeedle : this._x[m.channel];
      this._u.uNeedle.value[k] = (m.a0 + (m.a1 - m.a0) * x) * Math.PI / 180;
    }

    // --- the lights: backlight with an incandescent waver, PEAK, POWER
    const L = this._u.uLight.value;
    L[1] = this._exact ? 1 : 1 - FLICKER * (0.5 + 0.5 * Math.sin(t * 7.3) * Math.sin(t * 2.9 + 1.0));
    L[2] = this._exact || inDrop || t < this._peakUntil[0] ? 1 : 0;
    L[3] = this._exact || inDrop || t < this._peakUntil[1] ? 1 : 0;
    L[4] = 1;

    // --- the reels: the supply reel (left) speeds up as its pack empties
    const progress = clamp01(profile.progress != null ? profile.progress : 0.5);
    const pack = (p) => Math.sqrt(REEL_PACK[0] * REEL_PACK[0] + p * (REEL_PACK[1] * REEL_PACK[1] - REEL_PACK[0] * REEL_PACK[0]));
    const reels = this._data.reels;
    const rSupply = pack(1 - progress) * reels[0].r, rTake = pack(progress) * reels[1].r;
    if (!this._exact) {
      this._reelAngle[0] += REEL_TAPE_SPEED / rSupply * dt;
      this._reelAngle[1] += REEL_TAPE_SPEED / rTake * dt;
      this._disc = (this._disc + RPM_33 * dt) % (Math.PI * 2);
    }
    for (let k = 0; k < 2; k++) this._u.uReel.value[k].w = this._reelAngle[k] % (Math.PI * 2);
    this._u.uDisc.value = this._disc;
  }

  dispose() {
    if (this._mesh) {
      this._mesh.onBeforeRender = null;
      if (this._scene) this._scene.remove(this._mesh);
      this._mesh.geometry.dispose(); this._mesh.material.dispose();
      this._mesh = null;
    }
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._scene = null;
  }
}
